import type { Ctx } from 'boardgame.io';
import { debug } from '../utils/debug';
import type { DetroitGameState, DetroitPlayer, PlayerId, ActionDiskState } from '../types';
import { PLAYER_COLORS } from '../types';
import { DETROIT_MAP_CONFIG, DETROIT_RULES } from '../../data/map/detroit_bankruptcy_solo/config';
import { loadDetroitMap, loadDetroitMapLinks } from './utils/mapLoader';
import {
  initializeDetroitCubes,
  initializeDetroitGoodsDisplay,
  placeDetroitInitialGoods
} from './utils/goodsSetup';

/**
 * Detroit Bankruptcy Soloプレイ用のゲーム初期状態を生成
 * @param _ctx Boardgame.io コンテキスト（未使用）
 * @returns 初期ゲーム状態（DetroitGameState）
 */
export function setupDetroitGame(_ctx: Ctx): DetroitGameState {
  // Detroit Solo Play: プレイヤーは常に1人（ID='0'）
  const playerId: PlayerId = '0';

  // Detroitプレイヤー初期化
  const soloPlayer: DetroitPlayer = {
    id: playerId,
    name: 'Solo Player',
    color: PLAYER_COLORS[0],                   // プレイヤーカラー
    cash: DETROIT_RULES.initialCash,           // 初期資金 $0
    incomeLevel: 0,                            // 収入レベル 0
    sharesIssued: DETROIT_RULES.initialShares, // 初期株式数 5（Detroit固有）
    locomotiveLevel: DETROIT_RULES.initialLocoLevel, // 初期機関車レベル 1リンク
    selectedAction: null,                      // 未選択
    hasPassed: false,                          // アクション選択パスしていない
    playerOrder: 1,                            // ソロプレイなので常に1
    isBankrupt: false,                         // 倒産していない
    orderBidAmount: 0,                         // Phase 2未使用（Detroitではスキップ）
    tilesPlacedThisTurn: 0,                    // Phase 4: 今ターン配置したタイル数
    hasPassedBuild: false,                     // Phase 4: 手番終了フラグ
    buildTrackHistory: [],                     // Phase 4: Cancel操作用の配置履歴
    hasUpgradedLocoThisTurn: false,            // Phase 5: 今ターン機関車強化済みフラグ
    goodsTransportedThisPhase: 0,              // Phase 5: 輸送回数
    turnsCompletedThisPhase: 0,                // Phase 5: 完了した手番数（0, 1, 2）
    goodsPlacedThisTurn: 0,                    // Phase 9: 今ターン配置した商品数
    newCityTilesPlacedThisTurn: 0,             // Phase 4: 今ターン配置した新都市タイル数

    // Detroit固有プロパティ
    currentTurnSharesIssued: 0,                // 今ターン発行した株式数
    hasExecutedIssueSharesMove: false,         // 株式発行Move実行済みフラグ
    finalScore: null,                          // 最終スコア（ゲーム終了前はnull）
    isVictorious: false                        // 勝利フラグ
  };

  // プレイヤーマップ
  const players: Record<PlayerId, DetroitPlayer> = {
    [playerId]: soloPlayer
  };

  // アクションディスク初期化（各アクション3枚）
  const actionDisks: ActionDiskState = {
    ENGINEER: DETROIT_RULES.initialDisksPerAction,
    LOCOMOTIVE: DETROIT_RULES.initialDisksPerAction,
    URBANIZATION: DETROIT_RULES.initialDisksPerAction
  };

  // マップデータ読み込み（Detroit）
  let map = loadDetroitMap();

  // 特殊リンク読み込み（フェリー/隣接）
  const { ferryLinks, adjacentLinks } = loadDetroitMapLinks();

  // 商品キューブ初期化（全5色）
  let cubesInBag = initializeDetroitCubes();

  // 商品補充表初期化（両側最上段に計14個）
  const { goodsDisplay, remainingCubes } = initializeDetroitGoodsDisplay(cubesInBag);
  cubesInBag = remainingCubes;

  // マップ上に初期商品配置（全都市に2個ずつ）
  const { updatedMap, remainingCubes: finalCubes } = placeDetroitInitialGoods(map, cubesInBag);
  map = updatedMap;
  cubesInBag = finalCubes;

  debug.log(`[setupDetroitGame] Map loaded: ${map.length} hexes, ${cubesInBag.length} cubes in bag`);
  debug.log(`[setupDetroitGame] Player initialized: cash=$${soloPlayer.cash}, shares=${soloPlayer.sharesIssued}`);
  debug.log(`[setupDetroitGame] Action disks: ${JSON.stringify(actionDisks)}`);

  // Detroitゲーム状態初期化
  return {
    mapName: 'Detroit Bankruptcy Solo',
    mapConfig: DETROIT_MAP_CONFIG,
    players,
    map,
    goodsDisplay,
    cubesInBag,
    removedCubes: [],
    completedLinks: [],              // 完成区間なし
    unfinishedSections: [],          // 未完成区間なし
    ferryLinks,                      // Detroit: フェリーリンク
    adjacentLinks,                   // Detroit: 隣接リンク
    isGameOver: false,               // ゲーム継続中
    undoCount: 0,                    // Undo to Turn Start 実行回数
    endReason: null,
    finalScores: {},

    // Detroit固有プロパティ
    actionDisks,                     // アクションディスク（各3枚）
    actualTurn: 0,                   // ゲームターン初期値は0（Phase 1で+1される）

    // 自動フェーズ用
    autoPhaseResult: null,           // 自動フェーズ処理結果
    completedAutoPhases: [],         // 完了した自動フェーズ履歴

    // Town Disk 管理
    townDisksRemaining: 8,           // Town Disk 残り枚数（初期値8）
    townDiskPlaced: {},              // 各 Town への配置記録

    // ML学習用
    moveLog: []                      // Move-Level Replay Log
  };
}
